import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../config/api';

interface Site {
  siteid: number;
  sitename: string;
  city?: string;
  state?: string;
  qbocustidno?: string;
}

interface User {
  id: number;
  username: string;
  email: string;
  terms_accepted?: boolean;
  email_verified?: boolean;
  is_admin?: boolean;
  first_name?: string;
  last_name?: string;
  sites?: Site[];
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  user_id: number;
  username: string;
  email: string;
  terms_accepted: boolean;
  email_verified: boolean;
  is_admin: boolean;
  first_name?: string;
  last_name?: string;
  sites?: Site[];
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (username: string, password: string, rememberMe: boolean) => Promise<LoginResponse>;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const logout = useCallback(() => {
    // Remove only authentication data, preserve user preferences (terms consent, view mode, etc.)
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    // Keep: termsConsentHash, productViewMode, quoteMode

    setUser(null);
    navigate('/login');
  }, [navigate]);

  const checkAuth = async () => {
    const token = localStorage.getItem('access_token');

    if (token) {
      try {
        // Try to fetch fresh user data from /me endpoint
        const response = await api.get('/users/me');
        const userData: User = response.data;
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } catch (error) {
        // Fallback to stored user data if /me fails
        const storedUserData = localStorage.getItem('user');
        if (storedUserData) {
          try {
            setUser(JSON.parse(storedUserData));
          } catch (e) {
            localStorage.clear();
            setUser(null);
          }
        } else {
          setUser(null);
        }
      }
    } else {
      setUser(null);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    checkAuth();
    
    // Listen for session expired events
    const handleSessionExpired = () => {
      logout();
    };
    
    window.addEventListener('auth:session-expired', handleSessionExpired);
    
    return () => {
      window.removeEventListener('auth:session-expired', handleSessionExpired);
    };
  }, [logout]);

  const login = async (username: string, password: string, rememberMe: boolean): Promise<LoginResponse> => {
    const response = await api.post('/auth/login', {
      username,
      password,
      remember_me: rememberMe,
    });

    // Store tokens
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);

    // Fetch full user data from /me endpoint
    try {
      const meResponse = await api.get('/users/me');
      const userData: User = meResponse.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    } catch (error) {
      // Fallback to login response data if /me fails
      const userData: User = {
        id: response.data.user_id,
        username: response.data.username,
        email: response.data.email,
        terms_accepted: response.data.terms_accepted,
        email_verified: response.data.email_verified,
        is_admin: response.data.is_admin,
        first_name: response.data.first_name,
        last_name: response.data.last_name,
        sites: response.data.sites,
      };

      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
    }

    // Return login response for T&C handling
    return response.data;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};